import type { Event, TicketTier, TicketTierFormInput } from "./types";

const fallbackTimeZone = "America/New_York";

export function formatMoney(cents: number, currency = "USD"): string {
  const normalizedCurrency = currency.toUpperCase();
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: normalizedCurrency
  }).format(cents / 100);
}

export function formatTicketPrice(cents: number, currency = "USD"): string {
  if (cents === 0) return "Free";
  return formatMoney(cents, currency);
}

export function formatEventDate(value: string, timeZone = fallbackTimeZone): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone
  }).format(new Date(value));
}

export function formatEventTimeRange(startsAt: string, endsAt?: string, timeZone = fallbackTimeZone): string {
  const starts = new Date(startsAt);
  const startLabel = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone
  }).format(starts);

  if (!endsAt) {
    return startLabel;
  }

  const ends = new Date(endsAt);
  const endLabel = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone
  }).format(ends);

  return `${startLabel} - ${endLabel}`;
}

export function getHostedByNames(hostedBy?: string[]): string[] {
  return (hostedBy ?? []).map((name) => name.trim()).filter(Boolean);
}

export function getFlyerFallbackLabel(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "ANTIX";
  return words
    .slice(0, 3)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

export function isEventUnavailable(event: Pick<Event, "status" | "salesStatus">): boolean {
  return event.status !== "live" || event.salesStatus !== "on_sale";
}

export function isWithinSaleWindow(saleStartsAt?: string, saleEndsAt?: string, now = Date.now()): boolean {
  if (saleStartsAt) {
    const startsAt = Date.parse(saleStartsAt);
    if (!Number.isNaN(startsAt) && startsAt > now) return false;
  }
  if (saleEndsAt) {
    const endsAt = Date.parse(saleEndsAt);
    if (!Number.isNaN(endsAt) && endsAt <= now) return false;
  }
  return true;
}

export function isTierPurchasableNow(
  event: Pick<Event, "status" | "salesStatus" | "capacity">,
  tier: Pick<TicketTier, "status" | "salesStatus" | "inventoryLimit" | "saleStartsAt" | "saleEndsAt">,
  now = Date.now()
): boolean {
  if (isEventUnavailable(event)) return false;
  if (tier.status !== "active") return false;
  if (tier.salesStatus !== "on_sale") return false;
  if (event.capacity !== undefined && event.capacity <= 0) return false;
  if (tier.inventoryLimit !== undefined && tier.inventoryLimit <= 0) return false;
  return isWithinSaleWindow(tier.saleStartsAt, tier.saleEndsAt, now);
}

export function isTierUnavailable(
  event: Pick<Event, "status" | "salesStatus" | "capacity">,
  tier: Pick<TicketTier, "status" | "salesStatus" | "inventoryLimit" | "saleStartsAt" | "saleEndsAt" | "checkoutUrl">,
  now = Date.now()
): boolean {
  return (
    !isTierPurchasableNow(event, tier, now)
  );
}

export function getTierCallToAction(
  event: Pick<Event, "status" | "salesStatus" | "capacity">,
  tier: Pick<TicketTier, "status" | "salesStatus" | "inventoryLimit" | "saleStartsAt" | "saleEndsAt" | "checkoutUrl">,
  eventPath?: string,
  isPrevious = false,
  now = Date.now()
): { label: string; disabled: boolean; href?: string } {
  if (isPrevious || event.status === "completed" || event.status === "archived") {
    return { label: "Past Event", disabled: true };
  }
  if (event.salesStatus === "sold_out") {
    return { label: "Sold Out", disabled: true };
  }
  if (event.salesStatus === "sales_paused" || event.status !== "live") {
    return { label: "Sales Paused", disabled: true };
  }
  if (tier.status !== "active" || tier.salesStatus === "sold_out") {
    return { label: "Sold Out", disabled: true };
  }
  if (tier.salesStatus === "sales_paused") {
    return { label: "Sales Paused", disabled: true };
  }
  const saleEndsAt = tier.saleEndsAt ? Date.parse(tier.saleEndsAt) : Number.NaN;
  if (!Number.isNaN(saleEndsAt) && now >= saleEndsAt) {
    return { label: "Sold Out", disabled: true };
  }
  if (!isWithinSaleWindow(tier.saleStartsAt, tier.saleEndsAt, now)) {
    return { label: "Unavailable", disabled: true };
  }
  if ((event.capacity !== undefined && event.capacity <= 0) || (tier.inventoryLimit !== undefined && tier.inventoryLimit <= 0)) {
    return { label: "Unavailable", disabled: true };
  }
  return eventPath ? { label: "Get Tickets", disabled: false, href: eventPath } : { label: "Get Tickets", disabled: false };
}

export function getEventStatusLabel(event: Pick<Event, "status" | "salesStatus">, isPrevious = false): string {
  if (isPrevious || event.status === "archived" || event.status === "completed") return "Past Event";
  if (event.salesStatus === "sold_out") return "Sold Out";
  if (event.salesStatus === "sales_paused") return "Sales Paused";
  return "On Sale";
}

export function formatPriceInput(cents: number): string {
  if (!Number.isFinite(cents) || cents <= 0) return "";
  return (cents / 100).toFixed(2);
}

export function parsePriceInput(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  const normalized = Number(trimmed.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(normalized) || normalized < 0) return 0;
  return Math.round(normalized * 100);
}

export function validateStripeCheckoutLink(url: string): string | undefined {
  const trimmed = url.trim();
  if (!trimmed) return undefined;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return "Enter a valid Stripe Checkout link.";
  }

  if (parsed.protocol !== "https:") {
    return "Stripe Checkout links must use HTTPS.";
  }

  if (parsed.username || parsed.password) {
    return "Stripe Checkout links cannot include credentials.";
  }

  if (parsed.hostname !== "buy.stripe.com" && parsed.hostname !== "checkout.stripe.com" && parsed.hostname !== "book.stripe.com") {
    return "Paste a buy.stripe.com, checkout.stripe.com, or book.stripe.com link.";
  }

  return undefined;
}

export function getTierPublishReadiness(tier: TicketTierFormInput): string[] {
  const issues: string[] = [];
  if (!tier.name.trim()) issues.push("Ticket name is required.");
  if (tier.priceCents < 0) issues.push(`Enter a valid price for ${tier.name || "this tier"}.`);
  const urlError = validateStripeCheckoutLink(tier.checkoutUrl ?? "");
  if (urlError) issues.push(`Add a valid Stripe Checkout link for ${tier.name || "this tier"}.`);
  return issues;
}
